<template>
  <div class="cf-flex cf-flex--column cf-flex--gap-10">
    <!-- NOTE: The "name" and "autocomplete" attributes might look funny and inconsistent
    but are driven by special values required by browsers to autocomplete:
    https://stackoverflow.com/questions/7223168/how-to-trigger-autofill-in-google-chrome -->

    <cf-text-input
      id="frmNameCC"
      v-model="cardDetails.nameOnCard"
      class="cc-name"
      type="text"
      label="Name on Card"
      name="ccname"
      autocomplete="cc-name"
      data-three-ds="cardHolderName"
      :validations="{
        full_name: true,
        required: !skipRequiredValidation
      }"
      @focus="inputFocusHandler"
    />

    <cf-text-input
      id="frmCCNum"
      v-model="cardDetails.cardNumber"
      class="cc-number"
      icon-class="fa-credit-card"
      type="card"
      label="Credit Card Number"
      name="cardnumber"
      autocomplete="cc-number"
      data-three-ds="pan"
      :validations="{
        required: !skipRequiredValidation
      }"
      @focus="inputFocusHandler"
    />

    <cf-country-select
      :model-value="billingAddress.countryCode"
      name="billingCountrySelector"
      :validations="{
        required: !skipRequiredValidation
      }"
      @update:model-value="countrySelectedHandler"
    />

    <div class="cf-flex cf-flex--card-details">
      <cf-text-input
        id="frmCCExp"
        v-model="cardDetails.cardExpiration"
        class="cc-expiration"
        type="text"
        label="Expiration"
        name="exp-date"
        mask="cardExpiration"
        inputmode="numeric"
        placeholder="MM/YY"
        autocomplete="cc-exp"
        :validations="{
          required: !skipRequiredValidation,
          card_expiration: true
        }"
        @focus="inputFocusHandler"
      />

      <cf-text-input
        id="frmCCCVC"
        v-model="cardDetails.cvv"
        class="cc-cvv"
        type="cvv"
        label="CVV"
        name="cvc"
        autocomplete="cc-csc"
        :validations="{
          required: !skipRequiredValidation,
          cvv: {
            cardNumber: cardDetails.cardNumber
          }
        }"
        @focus="inputFocusHandler"
      />

      <cf-text-input
        v-if="!requireBillingAddress"
        v-model="billingAddress.postalCode"
        class="cc-zip"
        type="text"
        :label="postalCodeLabel"
        name="zip"
        data-three-ds="billingPostalCode"
        :mask="{ type: 'postalCode', region: billingAddress.countryCode }"
        :validations="postalCodeValidations"
        @focus="inputFocusHandler"
      />
    </div>

    <!-- These hidden inputs are needed for Paay 3ds auth https://docs.3dsintegrator.com/docs/dom-elements -->
    <template v-if="renderPaayTdsInputs">
      <cf-text-input
        v-show="false"
        type="text"
        label="month"
        name="month"
        :model-value="tdsMonth"
        data-three-ds="month"
      />

      <cf-text-input
        v-show="false"
        type="text"
        label="year"
        name="year"
        :model-value="tdsYear"
        data-three-ds="year"
      />

      <!--
        Hidden Address/Shipping details. Rendered for AMEX cards by default:
        for non-AMEX cards, sending the store's address as the shipping
        address (the default for dine-in/pickup) triggers AVS mismatch
        declines. Merchants can opt back into sending shipping for all
        brands by setting `includeShippingAddressForAllCards` on their
        threeDsConfig. The Paay SDK is initialized lazily at submit time
        (see the parent cf-payment-information component) so it scans the
        form with whatever shipping inputs Vue has rendered at that moment.
      -->
      <template v-if="isAmex || includeShippingAddressForAllCards">
        <cf-text-input
          v-show="false"
          type="text"
          label="shippingLine1"
          name="shippingLine1"
          :model-value="shippingDetails.line1"
          data-three-ds="shippingLine1"
        />

        <cf-text-input
          v-show="false"
          type="text"
          label="shippingLine2"
          name="shippingLine2"
          :model-value="shippingDetails.line2"
          data-three-ds="shippingLine2"
        />

        <cf-text-input
          v-show="false"
          type="text"
          label="shippingLine3"
          name="shippingLine3"
          :model-value="shippingDetails.line3"
          data-three-ds="shippingLine3"
        />

        <cf-text-input
          v-show="false"
          type="text"
          label="shippingPostCode"
          name="shippingPostCode"
          :model-value="shippingDetails.postalCode"
          data-three-ds="shippingPostCode"
        />

        <cf-text-input
          v-show="false"
          type="text"
          label="shippingCity"
          name="shippingCity"
          :model-value="shippingDetails.city"
          data-three-ds="shippingCity"
        />

        <cf-text-input
          v-show="false"
          type="text"
          label="shippingState"
          name="shippingState"
          :model-value="shippingDetails.state"
          data-three-ds="shippingState"
        />

        <cf-text-input
          v-show="false"
          type="text"
          label="shippingCountry"
          name="shippingCountry"
          :model-value="shippingDetails.countryCode"
          data-three-ds="shippingCountry"
        />

        <!--
          merchantRiskIndicator inputs. Gated alongside the shipping inputs
          because the indicator object only makes sense when a shipping
          address is being sent (the indicators describe HOW the order is
          shipping). Paay's SDK only emits the merchantRiskIndicator object
          if these inputs are in the DOM at construction time, so wrapping
          them in this v-if keeps them out of the non-AMEX payload.
        -->
        <cf-text-input
          v-show="false"
          model-value="04"
          type="text"
          label="shipIndicator"
          name="shipIndicator"
          data-three-ds="shipIndicator"
        />

        <cf-text-input
          v-show="false"
          model-value="02"
          type="text"
          label="deliveryTimeFrame"
          name="deliveryTimeFrame"
          data-three-ds="deliveryTimeFrame"
        />

        <cf-text-input
          v-show="false"
          model-value="01"
          type="text"
          label="reorderItemsInd"
          name="reorderItemsInd"
          data-three-ds="reorderItemsInd"
        />
      </template>

    </template>

    <div
      v-if="requireBillingAddress"
      class="cf-flex cf-flex--column cf-flex--gap-12"
    >
      <h4 class="cf-title cf-title--4 cf-text-align-left">
        Billing Details
      </h4>

      <div class="cf-flex cf-flex--column cf-flex--gap-10">
        <cf-text-input
          v-if="billingCountryNumeric"
          v-show="false"
          :model-value="billingCountryNumeric"
          type="text"
          label="billingCountry"
          name="billingCountry"
          data-three-ds="billingCountry"
        />

        <cf-text-input
          v-model="billingAddress.line1"
          type="text"
          autocomplete="address-line1"
          label="Street Address"
          placeholder="Street Address"
          name="addressLine1"
          data-three-ds="billingLine1"
          class="address-line-1"
          @focus="inputFocusHandler"
        />

        <cf-text-input
          v-model="billingAddress.line2"
          type="text"
          autocomplete="address-line2"
          label="Address Line 2"
          placeholder="Suite or apartment #"
          name="addressLine2"
          data-three-ds="billingLine2"
          class="address-line-2"
          @focus="inputFocusHandler"
        />

        <cf-text-input
          v-if="showSublocality"
          v-model="billingAddress.sublocality"
          type="text"
          autocomplete="address-level3"
          :label="labels.sublocality"
          name="sublocality"
          class="address-sublocality"
          :validations="sublocalityValidations"
          @focus="inputFocusHandler"
        />

        <cf-text-input
          v-if="showCity"
          v-model="billingAddress.city"
          type="text"
          autocomplete="address-level2"
          :label="labels.city"
          name="city"
          data-three-ds="billingCity"
          :validations="cityValidations"
          @focus="inputFocusHandler"
        />

        <div
          v-if="showState || showPostalCode"
          :class="stateAndPostalLayoutClass"
        >
          <template v-if="showState">
            <cf-select-input
              v-if="stateOptions"
              :model-value="billingAddress.state"
              :values="stateOptions"
              :label="labels.state"
              name="state"
              :filterable="true"
              :validations="stateValidations"
              @update:model-value="stateSelectedHandler"
            />

            <cf-text-input
              v-else
              v-model="billingAddress.state"
              type="text"
              autocomplete="address-level1"
              :label="labels.state"
              name="state"
              data-three-ds="billingState"
              :validations="stateValidations"
              @focus="inputFocusHandler"
            />

            <!--
              Paay 3DS reads billingState from a DOM input via the data-three-ds
              attribute (https://docs.3dsintegrator.com/docs/dom-elements).
              cf-select-input doesn't expose its underlying input element, so
              this hidden mirror carries the attribute for the Paay SDK to read
              when the state field is rendered as a dropdown.
            -->
            <cf-text-input
              v-if="renderPaayTdsInputs && stateOptions"
              v-show="false"
              type="text"
              label="state"
              name="paayBillingState"
              :model-value="billingAddress.state"
              data-three-ds="billingState"
            />
          </template>

          <cf-text-input
            v-if="showPostalCode"
            v-model="billingAddress.postalCode"
            type="text"
            :label="postalCodeLabel"
            name="zip"
            data-three-ds="billingPostCode"
            :mask="{ type: 'postalCode', region: billingAddress.countryCode }"
            :validations="postalCodeValidations"
            @focus="inputFocusHandler"
          />
        </div>
      </div>
    </div>
  </div>
</template>


<script>
  import { mapGetters } from 'vuex';
  import detectCreditCardType from 'credit-card-type';

  import returnAppConfig from '@/helpers/return-app-config';
  import { getCountryMeta } from '@/helpers/address-format';
  import { detectDefaultCountry } from '@/helpers/detect-default-country';
  import countryCodeToNumeric from '@/helpers/country-code-to-numeric';

  import CfCountrySelect from '@/view/components/shared/cf-country-select/index.vue';
  import CfSelectInput from '@/view/components/shared/cf-select-input/index.vue';
  import CfTextInput from '@/view/components/shared/cf-text-input/index.vue';


  export default {
    name: 'CfDefaultCreditCardForm',

    components: {
      CfCountrySelect,
      CfSelectInput,
      CfTextInput
    },

    inject: {
      $validator: {
        from: '$validator'
      }
    },

    props: {
      isCheckoutFlow: {
        type: Boolean,
        required: false,
        default: true
      },

      requireBillingAddress: {
        type: Boolean,
        default: false
      },

      tdsProvider: {
        type: String,
        default: ''
      },

      includeShippingAddressForAllCards: {
        type: Boolean,
        default: false
      },

      skipRequiredValidation: {
        type: Boolean,
        default: false
      }
    },

    data() {
      return {
        cardDetails: {
          nameOnCard: null,
          cardNumber: null,
          cardExpiration: null,
          cvv: null
        },
        billingAddress: {
          city: null,
          countryCode: detectDefaultCountry(),
          line1: null,
          line2: null,
          state: null,
          sublocality: null,
          postalCode: null
        },
        tdsYear: null,
        tdsMonth: null
      };
    },

    computed: {
      ...mapGetters('session', ['orderDetails']),
      ...mapGetters('locations', ['selectedLocation']),

      meta() {
        return getCountryMeta(this.billingAddress.countryCode);
      },

      labels() {
        return {
          city: this.meta.labels.city,
          state: this.meta.labels.state,
          sublocality: this.meta.labels.sublocality
        };
      },

      showCity() {
        return this.meta.fmt.includes('%C');
      },

      showState() {
        return this.meta.fmt.includes('%S');
      },

      showPostalCode() {
        return this.meta.fmt.includes('%Z');
      },

      showSublocality() {
        return this.meta.fmt.includes('%D');
      },

      stateOptions() {
        if (!this.meta.states) return null;
        return this.meta.states.map(s => ({ value: s.key, display: s.name }));
      },

      cityValidations() {
        return { required: !this.skipRequiredValidation && this.meta.require.city };
      },

      stateValidations() {
        return { required: !this.skipRequiredValidation && this.meta.require.state };
      },

      sublocalityValidations() {
        return { required: !this.skipRequiredValidation && this.meta.require.sublocality };
      },

      postalCodeLabel() {
        return this.meta.labels.zip;
      },

      postalCodeValidations() {
        const validations = { required: !this.skipRequiredValidation };
        if (this.meta.postalCodeRegex) {
          validations.regex = this.meta.postalCodeRegex;
        }
        return validations;
      },

      stateAndPostalLayoutClass() {
        return this.showState && this.showPostalCode
          ? 'cf-grid cf-grid--2-grid-column-equal cf-grid--gap-10'
          : 'cf-flex cf-flex--column cf-flex--gap-10';
      },

      renderPaayTdsInputs() {
        return this.isCheckoutFlow && this.tdsProvider === 'Paay';
      },

      isAmex() {
        const cardNumber = (this.cardDetails.cardNumber || '').replace(/[^0-9]/g, '');
        if (!cardNumber) return false;
        const matches = detectCreditCardType(cardNumber);
        return matches.length === 1 && matches[0].type === 'american-express';
      },

      billingCountryNumeric() {
        return countryCodeToNumeric(this.billingAddress.countryCode);
      },

      shippingDetails() {
        if (!this.isAmex && !this.includeShippingAddressForAllCards) return null;

        const { isTestEnvironment } = returnAppConfig();

        const shippingDetails = {
          countryCode: countryCodeToNumeric('US') || '840',
          shipIndicator: '04'
        };

        switch (this.orderDetails.fulfillmentType) {
          case 'delivery':
          case 'shipping': {
            const address = this.orderDetails.deliveryAddress;
            Object.assign(shippingDetails, {
              line1: address.line1,
              line2: address.line2,
              postalCode: address.postalCode,
              city: address.city,
              state: address.state,
              countryCode: countryCodeToNumeric(address.countryCode) || shippingDetails.countryCode,
              shipIndicator: '03'
            });
            break;
          }
          default: {
            const { address } = this.selectedLocation;
            Object.assign(shippingDetails, {
              line1: address.line1,
              line2: address.line2,
              postalCode: address.postalCode,
              city: address.city,
              state: address.state,
              countryCode: countryCodeToNumeric(address.countryCode) || shippingDetails.countryCode
            });
            break;
          }
        }
        /** This is used for testing purposes so QA can validate */
        // eslint-disable-next-line no-console
        if (isTestEnvironment && this.renderPaayTdsInputs) console.log(shippingDetails);

        return shippingDetails;
      }
    },

    watch: {
      'cardDetails.cardExpiration': 'cardExpirationChangeHandler',
      'billingAddress.state': 'transformToUpperCase'
    },

    methods: {
      inputFocusHandler() {
        this.$emit('interacted');
      },

      countrySelectedHandler(newCode) {
        if (!newCode || newCode === this.billingAddress.countryCode) return;

        this.billingAddress.countryCode = newCode;
        this.wipeFieldsHiddenInCountry();
      },

      stateSelectedHandler(newState) {
        this.billingAddress.state = newState;
      },

      /*
        Clears fields that aren't part of the current country's format string.
        Matches the cf-address-form pattern: "every time a field is hidden
        because it's not needed for a country, the input the user had entered
        for the field is wiped out."
      */
      wipeFieldsHiddenInCountry() {
        const { fmt } = getCountryMeta(this.billingAddress.countryCode);
        if (!fmt.includes('%S')) this.billingAddress.state = null;
        if (!fmt.includes('%C')) this.billingAddress.city = null;
        if (!fmt.includes('%Z')) this.billingAddress.postalCode = null;
        if (!fmt.includes('%D')) this.billingAddress.sublocality = null;
      },

      cardExpirationChangeHandler(value) {
        const month = Number(value.substring(0, 2));
        const year = Number(`20${value.substring(3)}`);

        /** Update tds values needed by Paay 3ds sdk to authorize payment */
        this.tdsYear = year;
        this.tdsMonth = month;
      },

      transformToUpperCase(value) {
        if (value && typeof value === 'string') {
          const upperCaseValue = value.toUpperCase();
          if (upperCaseValue !== value) {
            this.billingAddress.state = upperCaseValue;
          }
        }
      },

      normalizeNameOnCreditCard() {
        const fullName = this.cardDetails.nameOnCard;
        const nameSegments = fullName.trim().split(' ');

        const updatedCardHolderName = {
          firstName: nameSegments.shift(),
          lastName: nameSegments.join(' '),
          fullName
        };

        return updatedCardHolderName;
      },

      normalizeCardNumber() {
        const cardNumber = this.cardDetails.cardNumber || '';
        const unformattedCardNumber = cardNumber.replace(/[^0-9]/g, '');
        const cardType = detectCreditCardType(unformattedCardNumber)[0].niceType;

        const upatedCardNumbers = {
          cardNumber: unformattedCardNumber,
          cardNumberLastFour: unformattedCardNumber.slice(-4),
          cardType
        };

        return upatedCardNumbers;
      },

      normalizeCardExpiration() {
        const { cardExpiration } = this.cardDetails;
        const month = Number(cardExpiration.substring(0, 2));
        const year = Number(`20${cardExpiration.substring(3)}`);

        const updatedCardExpiration = {
          month,
          year
        };

        return updatedCardExpiration;
      },

      normalizeCardDetails() {
        const nameOnCard = this.normalizeNameOnCreditCard();
        const { cardNumber, cardNumberLastFour, cardType } = this.normalizeCardNumber();
        const cardExpiration = this.normalizeCardExpiration();

        const updatedCardDetails = {
          nameOnCard,
          cardNumber,
          cardNumberLastFour,
          cardType,
          cvv: this.cardDetails.cvv,
          cardExpiration
        };

        return updatedCardDetails;
      },

      submit() {
        const cardDetails = this.normalizeCardDetails();

        const { postalCode, countryCode, ...rest } = this.billingAddress;

        const billingAddress = {
          countryCode,
          postalCode,
          ...this.requireBillingAddress && { ...rest }
        };

        const paymentMethod = {
          type: 'card',
          cardDetails,
          billingAddress
        };

        this.$emit('success', { paymentMethod });
      }
    }
  };
</script>
